import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { LoadingButton } from "@src/appV2/lib/LoadingButton/LoadingButton";
import { Text, Title } from "@src/appV2/redesign/system/ui-components";
import {
  type SkillsAssessment,
  type SkillsAssessmentStatus,
} from "@src/appV2/SkillsAssessment/types";

import { LIMIT_SHIFTS_ACCESS_BASED_ON_TIMELINESS_EXPERIMENT_TYPE } from "../../constants";
import { OrientationListItems } from "../OrientationListItems";

interface Props {
  orientation: SkillsAssessment;
  orientationStatus: SkillsAssessmentStatus;
  timeToCompleteInMinutes: number;
  onStart: () => Promise<void>;
  isStarting: boolean;
}

export function ClipboardOrientationVideoStartStep(props: Props) {
  const { orientation, orientationStatus, timeToCompleteInMinutes, onStart, isStarting } = props;
  const onTimeClipboardScoreAdjustment =
    orientation.attributes.experimentDetails?.experimentMetadata?.onTimeReward
      .clipboardScoreAdjustment;
  const isLimitedAccessExperimentEnabled =
    orientation.attributes.experimentDetails?.experimentType ===
      LIMIT_SHIFTS_ACCESS_BASED_ON_TIMELINESS_EXPERIMENT_TYPE &&
    isDefined(onTimeClipboardScoreAdjustment);
  const { isStartedOnTime } = orientationStatus.attributes;
  return (
    <Stack flex={1} pt={6} justifyContent="space-between">
      <Stack>
        <Stack spacing={4}>
          <Title bold variant="h3" component="h3">
            Time for your Demo Shift!
          </Title>

          <Text light variant="body1">
            Your Demo Shift is starting now.
          </Text>
        </Stack>

        <OrientationListItems
          items={[
            isStartedOnTime
              ? {
                  text: `You are on time! Complete your Demo Shift now to show your punctuality ${
                    isLimitedAccessExperimentEnabled
                      ? `and gain ${onTimeClipboardScoreAdjustment} Clipboard Score points`
                      : ""
                  }.`,
                }
              : {
                  text: `The time you scheduled has passed and you can take it now. Join promptly to avoid showing up any later.`,
                },

            {
              text: `It is a quick video walkthrough and only takes ${timeToCompleteInMinutes} minutes.`,
            },
            {
              text: `You'll need to complete the Demo Shift to book shifts at most workplaces.`,
            },
          ]}
          sx={{
            py: 6,
          }}
        />
      </Stack>

      <LoadingButton
        isLoading={isStarting}
        variant="contained"
        sx={{
          borderRadius: 84,
          width: "100%",
        }}
        color="primary"
        onClick={onStart}
      >
        Begin
      </LoadingButton>
    </Stack>
  );
}
